/**
 * Legend for the Land Change layer, floating over the map.
 *
 * On the map rather than in the sidebar because it is a reading aid for what is
 * on screen — you need it in your eye-line while looking at the polygons, not
 * behind a collapsed panel. Colours come from LAND_CHANGE_ERAS and
 * LAND_GREENING_ERAS, the same lists the server SLD is built from, so the key
 * cannot drift from the map.
 *
 * It can be minimised: eleven swatches plus a note is a lot of map to cover once
 * you already know the scale. Collapsing leaves a small titled bar you can
 * reopen, and the choice is remembered per browser — someone who has learnt the
 * colours should not have to re-collapse it every visit.
 */

import React, { useState } from 'react';
import { LAND_CHANGE_ERAS, LAND_GREENING_ERAS, SHOW_GREENING } from './sentinelSource';
import { useRightInset } from './useRightInset';

const COLLAPSE_KEY = 'goldfinder.landchange.legend.collapsed';

const readCollapsed = () => {
  try { return window.localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
};
const writeCollapsed = (v) => {
  try { window.localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0'); } catch { /* private mode */ }
};

const styles = {
  panel: {
    position: 'absolute',
    zIndex: 3,
    background: 'rgba(255,255,255,0.96)',
    borderRadius: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
    padding: '9px 12px 10px',
    font: '12px system-ui, sans-serif',
    pointerEvents: 'auto',
    maxWidth: 210,
  },
  head: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  },
  title: { fontWeight: 600, fontSize: 12 },
  toggle: {
    border: 0, background: 'none', cursor: 'pointer', padding: '0 0 0 6px',
    color: '#8a8a8a', font: '600 13px/1 system-ui, sans-serif', flex: '0 0 auto',
  },
  row: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 },
  swatch: { display: 'inline-block', width: 22, height: 11, borderRadius: 2, flex: '0 0 auto' },
  label: { color: '#4a403a' },
  note: { color: '#8a8a8a', fontSize: 10.5, marginTop: 7, lineHeight: 1.45 },
  // Collapsed: a compact strip carrying a hint of the ramp, so it still reads as
  // the land-change key rather than an anonymous button.
  miniRamp: { display: 'flex', gap: 2, marginTop: 6 },
  miniSwatch: { width: 12, height: 8, borderRadius: 2 },
};

export function LandChangeLegend({ bottomOffset = 44 }) {
  const rightInset = useRightInset();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = () => {
    setCollapsed(prev => { writeCollapsed(!prev); return !prev; });
  };

  return (
    <div style={{ ...styles.panel, right: rightInset + 12, bottom: bottomOffset }}>
      <div style={styles.head}>
        <span style={styles.title}>
          {collapsed ? 'Land change' : 'Year vegetation was lost'}
        </span>
        <button
          type="button"
          onClick={toggle}
          style={styles.toggle}
          aria-expanded={!collapsed}
          title={collapsed ? 'Show the land change legend' : 'Minimise the legend'}
        >
          {collapsed ? '+' : '–'}
        </button>
      </div>

      {collapsed ? (
        <div style={styles.miniRamp} aria-hidden="true">
          {[...LAND_CHANGE_ERAS, ...(SHOW_GREENING ? LAND_GREENING_ERAS : [])].map(era => (
            <span key={`m${era.from}${era.colour}`}
                  style={{ ...styles.miniSwatch, background: era.colour }} />
          ))}
        </div>
      ) : (
        <>
          {LAND_CHANGE_ERAS.map(era => (
            <div key={era.from} style={styles.row}>
              <span style={{ ...styles.swatch, background: era.colour }} />
              <span style={styles.label}>{era.from}&ndash;{era.to}</span>
            </div>
          ))}
          {SHOW_GREENING && (
            <>
              <div style={{ ...styles.title, marginTop: 9, marginBottom: 6 }}>Year it got greener</div>
              {LAND_GREENING_ERAS.map(era => (
                <div key={`g${era.from}`} style={styles.row}>
                  <span style={{ ...styles.swatch, background: era.colour }} />
                  <span style={styles.label}>{era.from}&ndash;{era.to}</span>
                </div>
              ))}
            </>
          )}
          <div style={styles.note}>
            30 m, from annual Landsat cover.{SHOW_GREENING
              ? ' Green over a cleared patch means it is growing back, not a standing clearing.'
              : ''} Click a polygon for its details.
          </div>
        </>
      )}
    </div>
  );
}
